import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const leadership = [
    {
        name: "Dr. Prateek Grewal",
        tag: "Partner",
        role: "Partner — Strategic Advisory",
        bio: "Dr. Prateek Grewal is a specialist in high-value, cross-border transaction structuring with experience across defence procurement, global commodities, and trade finance. He operates at the intersection of strategy and execution, with a strong network spanning government institutions, financial entities, and international suppliers. His expertise lies in aligning complex multi-party transactions and driving deals to successful closure.",
        img: "https://customer-assets.emergentagent.com/job_execution-hub-52/artifacts/ybx6o80x_281BC4BD-D717-4E69-A4A3-CF32E2D117F3.png",
        focus: ["Defence Procurement", "Commodities & Trade", "Trade Finance"],
    },
    {
        name: "Dr. Aman Grewal",
        tag: "Partner",
        role: "Partner — Public Health & Strategic Advisory",
        bio: "Dr. Aman Grewal (MBBS, PhD) is a medical professional and public health specialist with expertise in healthcare systems, policy alignment, and large-scale program execution. She brings a structured, analytical approach to strategic planning and operational oversight, particularly in sectors involving healthcare, public infrastructure, and social impact initiatives. Her role strengthens Indravax's capabilities in health-related advisory, institutional engagement, and interdisciplinary projects requiring medical and regulatory insight.",
        img: "https://customer-assets.emergentagent.com/job_execution-hub-52/artifacts/z4ktml7t_91BAACC8-E8D0-4958-8E7B-AA0C3E192BC8.png",
        focus: ["Public Health", "Policy & Regulation", "Program Execution"],
    },
    {
        name: "Dilmanpreet Singh Makkar",
        tag: "COO",
        role: "Chief Operating Officer",
        bio: "Dilman is a globally focused oil and energy executive with nine years of experience identifying, structuring, and advancing high-value opportunities across international markets. A commercially driven entrepreneur, he is recognised for converting high-conviction opportunities into scalable, resilient, and enduring enterprises. Combining strategic foresight with disciplined execution, Dilman has built and led ventures across complex and rapidly evolving markets. His leadership is defined by decisive action, intelligent risk management, strong governance, and an uncompromising focus on results. With a worldwide commercial presence and an expanding network of international partners, he develops robust teams, trusted cross-border relationships, and execution-ready business ecosystems — accelerating growth, creating lasting enterprise value and delivering measurable results on a global scale.",
        img: "https://customer-assets-wrfwihn1.emergentagent.net/job_execution-hub-52/artifacts/0ib2bwdg_27EB5F7E-A264-45AE-82D2-1C66D700C380.png",
        focus: ["Oil & Energy", "Global Growth", "Operations & Governance"],
    },
];

const About = () => {
    return (
        <div className="page-enter">
            <SEO
                title="About — Independent Strategic Advisory Firm"
                description="Indravax Strategic Advisory LLP is an independent multi-sector advisory firm engaged in strategic consulting and transaction facilitation across defence, trade, energy, and healthcare globally."
                path="/about"
                keywords="indravax about, strategic advisory firm india, independent advisory firm, leadership, advisory network"
            />
            {/* Hero */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden" data-testid="about-hero">
                <GlobeLines className="opacity-15" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 w-full relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Overline>About the Firm</Overline>
                        <h1 className="font-serif text-5xl md:text-7xl text-white leading-[1.02] mt-6 max-w-5xl">
                            Structured advisory across <br />
                            <span className="text-[#C9A14A] italic font-light">defence, trade, energy,</span> and healthcare.
                        </h1>
                        <p className="mt-8 text-lg text-slate-400 font-light max-w-2xl leading-relaxed">
                            An independent, multi-sector advisory firm engaged in strategic consulting and
                            transaction facilitation across global markets.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Overview */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="about-manifesto">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-4">
                        <Overline>Overview</Overline>
                        <h2 className="font-serif text-4xl text-white mt-6 leading-[1.1]">
                            A multi-sector advisory firm operating across global markets.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-[1.9]">
                        <p>
                            Indravax Strategic Advisory LLP is a multi-sector advisory firm engaged in
                            strategic consulting and transaction facilitation across global markets.
                        </p>
                        <p>
                            The firm operates at the intersection of <span className="text-[#C9A14A]">advisory,
                            market access, financial structuring,</span> and execution support — combining
                            strategic insight with facilitation capability to help clients navigate complex
                            transactions and multi-jurisdictional environments.
                        </p>
                        <p className="text-slate-400 italic border-l-2 border-[#C9A14A]/60 pl-5">
                            Indravax provides advisory and facilitation services and does not engage in
                            regulated activities without obtaining the necessary approvals.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Principles */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="about-principles">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Operating Principles"
                        title="Four commitments that govern every engagement."
                        testId="about-principles-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#C9A14A]/15">
                        {[
                            { t: "Confidentiality", d: "Every engagement operates under strict NDA and regulated information flow." },
                            { t: "Advisory Integrity", d: "We provide advisory and facilitation — never acting beyond our mandated scope." },
                            { t: "Alignment", d: "Stakeholders, instruments, and jurisdictions coordinated as a single sequence." },
                            { t: "Compliance", d: "All activity sits squarely within applicable regulatory frameworks." },
                        ].map((p, i) => (
                            <FadeUp key={p.t} delay={i * 0.08} className="bg-[#040B1A] p-10 md:p-14">
                                <div className="font-serif text-5xl text-[#C9A14A] italic">0{i + 1}</div>
                                <h3 className="font-serif text-2xl text-white mt-6">{p.t}</h3>
                                <p className="text-slate-400 font-light mt-3 leading-relaxed">{p.d}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="py-24 md:py-32" data-testid="about-leadership">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Leadership Driving Execution"
                        title="Institutional depth, interdisciplinary range, operational discipline."
                        description="Indravax leadership combines expertise across strategic transactions, global trade, energy, public health systems and operational execution to deliver disciplined, outcome-led engagements."
                        testId="about-leadership-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
                        {leadership.map((p, i) => (
                            <FadeUp key={p.name} delay={i * 0.1}>
                                <div className="group h-full flex flex-col" data-testid={`leadership-card-${i}`}>
                                    <div className="aspect-[4/5] relative overflow-hidden border border-[#C9A14A]/20 bg-[#0A1F44]">
                                        <img
                                            src={p.img}
                                            alt={p.name}
                                            className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-105"
                                            style={{ filter: 'grayscale(15%) contrast(1.05)' }}
                                        />
                                        <div className="absolute inset-0 bg-[#0A1F44]/20 mix-blend-multiply pointer-events-none" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#040B1A] via-[#040B1A]/40 to-transparent" />
                                        <div className="absolute top-5 left-5 overline">{p.tag} · 0{i + 1}</div>
                                    </div>
                                    <div className="mt-8">
                                        <div className="overline text-[#C9A14A]">{p.role}</div>
                                        <h3 className="font-serif text-2xl md:text-3xl text-[#C9A14A] mt-3 leading-tight">{p.name}</h3>
                                        <p className="text-slate-300 mt-5 font-light leading-[1.85] text-sm md:text-base">{p.bio}</p>
                                        <div className="mt-7 flex flex-wrap gap-2">
                                            {p.focus.map((f) => (
                                                <span key={f} className="px-3 py-1 border border-[#C9A14A]/30 text-[0.65rem] tracking-[0.22em] uppercase text-[#C9A14A]">
                                                    {f}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                    </div>

                    <FadeUp delay={0.3} className="mt-20 max-w-3xl mx-auto text-center">
                        <div className="gold-mark mx-auto mb-6" />
                        <p className="font-serif text-xl md:text-2xl text-slate-300 italic leading-relaxed">
                            Indravax leadership combines expertise across strategic transactions, global trade, energy, public health systems and operational execution to deliver disciplined, outcome-led engagements.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Advisory Network */}
            <section className="py-24 md:py-32 bg-[#07132E]/40 relative overflow-hidden" data-testid="about-advisory-network">
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        overline="Advisory Network"
                        title="Global expertise supporting strategic execution."
                        description="Indravax draws on an advisory network of senior global operators across trade, energy, technology, and institutional markets."
                        testId="about-advisory-heading"
                    />

                    <FadeUp>
                        <div className="institutional-card grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 p-8 md:p-12" data-testid="advisor-card-ajay-parmar">
                            <div className="lg:col-span-4">
                                <div className="aspect-[4/5] relative overflow-hidden border border-[#C9A14A]/20 bg-[#0A1F44]">
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_execution-hub-52/artifacts/q4nqks14_b1f2d06d-f751-489b-9b7c-adf24badeee9.jpeg"
                                        alt="Ajay Parmar"
                                        className="w-full h-full object-cover"
                                        style={{ filter: 'grayscale(20%) contrast(1.05)' }}
                                    />
                                    <div className="absolute inset-0 bg-[#0A1F44]/25 mix-blend-multiply pointer-events-none" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#040B1A] via-[#040B1A]/30 to-transparent" />
                                    <div className="absolute top-5 left-5 overline">Advisor</div>
                                </div>
                            </div>

                            <div className="lg:col-span-8">
                                <div className="overline">International Business Development · Strategic Advisor</div>
                                <h3 className="font-serif text-4xl md:text-5xl text-[#C9A14A] mt-3 leading-[1.05]">
                                    Ajay Parmar
                                </h3>
                                <p className="mt-3 text-[0.7rem] tracking-[0.25em] uppercase text-slate-400">
                                    35+ Years · Global · Multi-Sector
                                </p>

                                <div className="mt-8 space-y-5 text-slate-300 font-light leading-[1.9]">
                                    <p>
                                        Ajay Parmar is a seasoned international business development professional
                                        with over <span className="text-[#C9A14A]">35 years of experience</span>
                                        {" "}across multiple industry verticals globally. He brings deep expertise
                                        in global public relations strategies, international trade, franchise
                                        consultancy, new business development, and global sales and marketing,
                                        with a strong track record of building and scaling businesses across
                                        markets.
                                    </p>
                                    <p>
                                        Ajay has held senior leadership positions across diverse sectors. He
                                        previously served as Vice President – Global Sales & Marketing at Center
                                        of Gravity Engineering Solutions Inc., where he was responsible for the
                                        company's global business launch. Prior to this, he held leadership
                                        roles including Director – International Trade and Vice President –
                                        International Trade at Sunclare India Pvt. Ltd., an Oberoi family
                                        enterprise.
                                    </p>
                                    <p>
                                        He was also Executive Vice President at Franchise Asia, a US–India joint
                                        venture and one of the earliest franchise consultancy platforms in India,
                                        where he played a key role in introducing structured franchise advisory
                                        services to the Indian market. In addition, Ajay served as Executive
                                        Vice President at Wackenhut Lancers Ltd., contributing to the
                                        development of corporate security services and solutions in India.
                                    </p>
                                    <p>
                                        Ajay's international exposure spans India, the Middle East, Mauritius,
                                        New Zealand, Australia, and the United States. He has worked across
                                        sectors including engineering, security services, oil & gas, and
                                        technology.
                                    </p>
                                    <p>
                                        Currently, Ajay is associated with <span className="text-white">Pro One, USA</span>,
                                        a manufacturer of advanced lubricant solutions serving the global oil &
                                        gas industry. Since 2020, he has also been serving as International
                                        Marketing Advisor to <span className="text-white">Bio Soilz</span>, a
                                        Dubai-based soil remediation company with operations in over 50
                                        countries. He is also engaged as a strategic advisor to
                                        {" "}<span className="text-white">JoopiterX</span>, a San Francisco-based
                                        AI startup focused on transforming the travel, hospitality, and events
                                        management sectors.
                                    </p>
                                    <p>
                                        Ajay holds a Master's degree in International Politics and is also a
                                        qualified corporate lawyer, bringing a unique combination of legal,
                                        strategic, and commercial insight to advisory engagements.
                                    </p>
                                </div>

                                <div className="mt-10 pt-6 border-t border-[#C9A14A]/15 flex flex-wrap gap-2">
                                    {["Trade", "Oil & Gas", "Franchise Advisory", "Security Services", "Technology", "International Business"].map((t) => (
                                        <span key={t} className="px-3 py-1.5 border border-[#C9A14A]/30 text-[0.65rem] tracking-[0.22em] uppercase text-[#C9A14A]">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.2} className="mt-14 max-w-3xl mx-auto text-center">
                        <p className="font-serif text-lg md:text-xl text-slate-300 italic leading-relaxed">
                            The advisory network of Indravax brings together global experience across trade,
                            energy, technology, and institutional markets to support complex, cross-border
                            engagements.
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.3} className="mt-10 max-w-3xl mx-auto">
                        <p className="text-xs text-slate-500 font-light text-center leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic">
                            Advisors are independent professionals and do not represent the firm in any
                            regulated capacity unless formally authorized.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Associated Companies */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="about-associated">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Associated Companies"
                        title="Partnerships that extend our capability."
                        description="Indravax works with a select group of associated companies across technology, data, and specialised advisory — extending its reach and delivery across sectors."
                        testId="about-associated-heading"
                    />

                    <FadeUp>
                        <a
                            href="https://www.particlesinfo.in"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group"
                            data-testid="associated-card-particles"
                        >
                            <div className="institutional-card grid grid-cols-1 lg:grid-cols-12 gap-10 p-8 md:p-12 hover:border-[#C9A14A]/60 transition-all duration-500">
                                {/* Logo block */}
                                <div className="lg:col-span-4 flex items-center justify-center">
                                    <div className="w-full aspect-square max-w-[240px] bg-white flex items-center justify-center p-8 border border-[#C9A14A]/20 group-hover:scale-[1.02] transition-transform duration-700">
                                        <img
                                            src="/assets/particles-logo.png"
                                            alt="Particles Infoscience"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="lg:col-span-8 flex flex-col justify-center">
                                    <div className="overline">Technology · Data & Information Science</div>
                                    <h3 className="font-serif text-3xl md:text-4xl text-[#C9A14A] mt-3 leading-[1.1]">
                                        Particles Infoscience
                                    </h3>
                                    <p className="mt-6 text-slate-300 font-light leading-[1.9]">
                                        A technology and information-science firm operating at the intersection
                                        of data, analytics, and digital transformation. Associated with Indravax
                                        to support technology-led advisory engagements and digital infrastructure
                                        across sectors.
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-2">
                                        {["Technology", "Data", "Information Science", "Digital Advisory"].map((t) => (
                                            <span key={t} className="px-3 py-1.5 border border-[#C9A14A]/30 text-[0.65rem] tracking-[0.22em] uppercase text-slate-300">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="mt-8 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] group-hover:gap-4 transition-all">
                                        Visit particlesinfo.in <ArrowRight size={12} />
                                    </div>
                                </div>
                            </div>
                        </a>
                    </FadeUp>

                    <FadeUp delay={0.2} className="mt-10 max-w-3xl mx-auto">
                        <p className="text-xs text-slate-500 font-light text-center leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic">
                            Associated companies operate as independent entities. Engagements with associated
                            companies are governed by their respective agreements and terms.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-28 border-t border-[#C9A14A]/15" data-testid="about-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <h2 className="font-serif text-3xl md:text-5xl text-white leading-[1.1]">
                        Evaluate an opportunity with an independent advisory firm.
                    </h2>
                    <Link to="/contact" className="btn-primary mt-10 inline-flex" data-testid="about-cta-btn">
                        Engage Indravax <ArrowRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default About;
